import React, { Fragment, FunctionComponent } from "react";
import styles from "./styles/Header.module.scss";

import { CLOSE_ALL_OVERLAYS, OPEN_MOBILE_MENU } from "layout/redux/overlay.actions";
import { useDispatch, useSelector } from "react-redux";
import Anchor from "components/Anchor/Anchor";
import combineClasses from "helpers/combine-classes";
import countriesIsActive from "../helpers/countries-is-active";
import countriesMenuHandler from "layout/helpers/countriesMenuHandler";
import CountriesOverlay from "./components/CountriesOverlay/CountriesOverlay";
import Dictionary from "components/Dictionary/Dictionary";
import IconViewer from "components/IconViewer/IconViewer";
import megaDropdownHandler from "layout/helpers/megaDropdownHandler";
import MobileMenu from "./components/MobileMenu/MobileMenu";
import navigationIsActive from "../helpers/navigation-is-active";
import productsIsActive from "../helpers/products-is-active";
import useTranslation from "components/Dictionary/hooks/useTranslation";
import useStickyHeader from "./hooks/useStickyHeader";

const Header: FunctionComponent = () => {
    // TODO: Add active page item class and styling.

    const overlay = useSelector((state: any) => state.overlay);
    const currentPage = useSelector((state: any) => state.page);
    const settings = useSelector((state: any) => state.settings.content);
    const translations = useTranslation();

    const dispatch = useDispatch();
    const { overThreshold } = useStickyHeader();

    const isStickyClass = overThreshold ? styles.is_sticky : undefined;

    const toggleMobileMenu = () => {
        if (overlay.currentOverlay === "MOBILE_MENU") {
            dispatch({ type: CLOSE_ALL_OVERLAYS });
        } else {
            dispatch({ type: OPEN_MOBILE_MENU });
        }
    };

    const getBannerBackgroundColor = () => {
        return (currentPage?.content as any)?.blocks?.[0]?.settings?.backgroundColor?.color || "";
    };

    const generateHeaderColors = () => {
        const bg = getBannerBackgroundColor();

        switch (bg) {
            case "#223d34":
                return { background: "#223d34", text: "#ffffff" };
            case "#f5f1e9":
                return { background: "#f5f1e9", text: "#223d34" };
            default:
                return { background: "#ffffff", text: "#223d34" };
        }
    };

    const megaDropDownActiveClass = overlay.currentOverlay === "MEGA_DROPDOWN_MENU" ? styles.mega_dropdown_active : "";
    const currentCulture = (currentPage.content as any)?._culture ?? (currentPage.content as any)?.language;

    return (
        <Fragment>
            <header
                className={combineClasses([styles.wrapper, isStickyClass, megaDropDownActiveClass])}
                style={
                    {
                        "--header-bg": generateHeaderColors().background,
                        "--header-text": generateHeaderColors().text,
                    } as React.CSSProperties
                }
            >
                <div className={combineClasses([styles.inner, isStickyClass])}>
                    {currentPage?.languageId === "en" ? (
                        <a
                            className={combineClasses([styles.shop_button, styles.only_mobile])}
                            href="https://b2b-international.easyfood.dk/account/"
                        >
                            <span className={styles.button_label}>Account</span>
                        </a>
                    ) : null}

                    {/* Products */}
                    <div className={combineClasses([styles.products_link, productsIsActive(currentPage.content, styles, overlay)])}>
                        {/* B2B Button */}
                        <a
                            className={styles.shop_button}
                            href="https://b2b-international.easyfood.dk/"
                        >
                            <span className={styles.button_label}>
                                <Dictionary name="HEADER.GO_TO_WEBSHOP" />
                            </span>
                        </a>
                    </div>

                    {currentPage?.languageId === "en" ? (
                        <div className={combineClasses([styles.products_link, productsIsActive(currentPage.content, styles)])}>
                            {currentPage?.languageId === "en" ? (
                                <a
                                    className={styles.shop_button}
                                    href="https://b2b-international.easyfood.dk"
                                >
                                    <span className={styles.button_label}>Products</span>
                                </a>
                            ) : null}
                        </div>
                    ) : null}

                    {/* Navigation */}
                    <nav className={styles.navigation}>
                        {/* Logo */}
                        <Anchor
                            to={currentPage?.languageUrl ?? "/"}
                            className={combineClasses([styles.logo_link, isStickyClass])}
                        >
                            <IconViewer
                                className={styles.logo}
                                name="logo"
                            />
                        </Anchor>
                        <div>
                            {settings?.menu &&
                                settings?.menu.map((item: any, index: number) => {
                                    if (!item) return null;
                                    const normalizedItem = item.content ? item : { content: item };
                                    const link = normalizedItem.content.link || "#";
                                    const label = normalizedItem.content.label;
                                    const subMenu = normalizedItem.content.subMenu;

                                    return (
                                        <div
                                            className={styles.navigation_item}
                                            key={index}
                                            onMouseEnter={(event) => megaDropdownHandler(event, normalizedItem, dispatch)}
                                            onMouseLeave={(event) => megaDropdownHandler(event, undefined, dispatch)}
                                        >
                                            <Anchor
                                                className={combineClasses([styles.navigation_link, navigationIsActive(normalizedItem, styles, overlay)])}
                                                to={link}
                                            >
                                                <span className={styles.navigation_label}>
                                                    {label}

                                                    {subMenu && subMenu.length > 0 ? (
                                                        <IconViewer
                                                            className={styles.chevron}
                                                            name="chevronDown"
                                                            heightRatio={7.805 / 12.781}
                                                        />
                                                    ) : null}
                                                </span>

                                                {subMenu && subMenu.length > 0 ? <span className={styles.cone}></span> : null}
                                            </Anchor>
                                        </div>
                                    );
                                })}

                            {/* Country */}
                            {currentPage.content?._hrefLang && Object.keys(currentPage.content._hrefLang).length > 1 ? (
                                <div
                                    className={combineClasses([styles.countries_link, countriesIsActive(currentPage.content, styles, overlay)])}
                                    onClick={(event) => countriesMenuHandler(event, currentPage, dispatch, overlay)}
                                >
                                    <span className={styles.countries_label}>
                                        <Dictionary
                                            name={
                                                currentPage.languageId ? `HEADER.COUNTRIES.${currentPage.languageId.toUpperCase()}` : "HEADER.COUNTRIES.UNKNOWN"
                                            }
                                        />
                                        <IconViewer
                                            className={styles.countries_chevron}
                                            name="chevronDown"
                                            heightRatio={7.805 / 12.781}
                                        />
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </nav>
                    {/* Logo */}
                    <Anchor
                        to={currentPage?.languageUrl ?? "/"}
                        className={combineClasses([styles.mobile_logo, isStickyClass])}
                    >
                        <IconViewer
                            className={styles.logo}
                            name="logo"
                        />
                    </Anchor>
                    {/* Menu Toggle */}
                    <button
                        className={styles.mobile_menu_button}
                        onClick={() => toggleMobileMenu()}
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                </div>

                {overlay.currentOverlay === "COUNTRIES_MENU" ? (
                    <CountriesOverlay
                        translations={translations}
                        {...currentPage.content}
                    />
                ) : null}
            </header>

            {/* TODO: Make a new MOBILE_MENU_CLOSING action to not have it close instant */}
            {overlay.currentOverlay === "MOBILE_MENU" ? (
                <MobileMenu
                    settings={settings}
                    menu={overlay.content}
                    translations={translations}
                    culture={currentCulture}
                    cultures={currentPage.content?._hrefLang}
                />
            ) : null}
        </Fragment>
    );
};

export default Header;
